/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import PropTypes from 'prop-types';

// @mui material components
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDButton from 'components/MDButton';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import signatureBlank from 'assets/images/signature-black.png';

const initCompany = {
  id: '',
  code: '',
  name: '',
  taxid: '',
  branch: '',
  business: '',
  fax: '',
  address: '',
  subdistrict_id: '',
  district_id: '',
  province_id: '',
  zipcode: '',
  tel: '',
  email: '',
  image_id: '',
  signature_id: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
  image: '',
  province_name: '',
  district_name: '',
  user_id: '',
  user_name: '',
  user_signature: '',
  today: Func.dateNow(),
};

function PurchaseRequestPrint({ parent, size }) {
  const mod = 'purchase_request';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [company, setCompany] = useState(initCompany);
  const [attr, setAttr] = useState({});
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);
  const promiseResolveRef = useRef(null);
  const limitRow = 10;

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true);
      });
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false);
    },
  });

  const print = () => {
    setLoading(true);
    setCompany({});
    setAttr({});
    setList([]);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      id: parent.id,
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCompany(result.company);
        setAttr(result.data);
        setList(result.list);

        setLoading(false);

        handlePrint();
      }
    });
  };

  const renderHeader = () => {
    return (
      <div>
        <table width='100%' cellPadding={5}>
          <tbody>
            <tr>
              <td width='30%'>
                <img src={company.image} alt={company.name} width='150' />
              </td>
              <td>
                <table width='100%' cellPadding={5}>
                  <tbody>
                    <tr>
                      <td>
                        <h3>{company.name}</h3>
                      </td>
                    </tr>
                    <tr>
                      <td width='85%'>
                        {company.address} {company.district_name} {company.province_name}{' '}
                        {company.zipcode} {company.branch}
                      </td>
                    </tr>
                    <tr>
                      <td>เลขประจำตัวผู้เสียภาษี : {company.taxid}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
        <table width='100%' cellPadding={5}>
          <tbody>
            <tr>
              <td align='center' colSpan={2}>
                <h2>ใบขอซื้อ (Purchase request)</h2>
              </td>
            </tr>
            <tr>
              <td width='50%'>&nbsp;</td>
              <td align='right'>NO. : {attr.code}</td>
            </tr>
            <tr>
              <td>&nbsp;</td>
              <td align='right'>วันที่ : {Func.dateDisplay(attr.date_record)}</td>
            </tr>
          </tbody>
        </table>
        <table width='100%' cellPadding={5}>
          <tbody>
            <tr>
              <td>&nbsp;</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderOrder = () => {
    return (
      <table className='table' width='100%' cellPadding={5} cellSpacing={4} border='1'>
        <thead>
          <tr>
            <th width='10%'>ลำดับที่</th>
            <th>รายการ</th>
            <th width='15%'>จำนวน</th>
            <th width='10%'>หน่วย</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, i) => (
            <tr key={item.id}>
              <td align='center' valign='top'>
                {i + 1}
              </td>
              <td valign='top'>{item.product_name}</td>
              <td align='right' valign='top'>
                {Func.numberDisplay(item.quantity)}
              </td>
              <td align='center' valign='top'>
                {item.unit_name}
              </td>
            </tr>
          ))}
          {list.length < limitRow
            ? Array.from(Array(limitRow - list.length)).map((_, index) => (
                <tr key={index.toString()}>
                  <td align='center'>{list.length + index + 1}</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))
            : null}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>หมายเหตุ : {attr.remark}</td>
          </tr>
        </tfoot>
      </table>
    );
  };

  const renderFooter = () => {
    return (
      <table width='100%'>
        <tbody>
          <tr>
            <td align='center' width='50%'>
              <table width='100%' cellSpacing={10}>
                <tbody>
                  <tr>
                    <td align='right' width='20%' valign='bottom'>
                      ลงชื่อ
                    </td>
                    <td align='center'>
                      <div className='underline-dot'>
                        {company.user_signature === '' ? (
                          <img src={signatureBlank} alt='Signature' width='150' />
                        ) : (
                          <img src={company.user_signature} alt={company.user_name} width='150' />
                        )}
                      </div>
                    </td>
                    <td width='40%' valign='bottom'>
                      ผู้ขอซื้อ
                    </td>
                  </tr>
                  <tr>
                    <td align='center'>&nbsp;</td>
                    <td align='center'>
                      <div className='underline-dot'>( {company.user_name} )</div>
                    </td>
                    <td align='center'>&nbsp;</td>
                  </tr>
                  <tr>
                    <td align='center'>&nbsp;</td>
                    <td align='center'>
                      <div className='underline-dot'>วันที่ {Func.dateDisplay(company.today)}</div>
                    </td>
                    <td align='center'>&nbsp;</td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td align='center'>
              <table width='100%' cellSpacing={10}>
                <tbody>
                  <tr>
                    <td align='right' width='20%' valign='bottom'>
                      ลงชื่อ
                    </td>
                    <td align='center'>
                      <div className='underline-dot'>
                        <img src={signatureBlank} alt={company.name} width='150' />
                      </div>
                    </td>
                    <td width='40%' valign='bottom'>
                      ผู้อนุมัติ
                    </td>
                  </tr>
                  <tr>
                    <td align='center'>&nbsp;</td>
                    <td align='center'>
                      <div className='underline-dot'>
                        ( &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                        &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; )
                      </div>
                    </td>
                    <td align='center'>&nbsp;</td>
                  </tr>
                  <tr>
                    <td align='center'>&nbsp;</td>
                    <td align='center'>
                      <div className='underline-dot'>
                        วันที่ &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp; / &nbsp; &nbsp; &nbsp;
                      </div>
                    </td>
                    <td align='center'>&nbsp;</td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderPrint = () => {
    return (
      <div style={{ display: 'none' }}>
        <div ref={printRef} className='print'>
          <div>
            <div className='a4'>
              {renderHeader()}
              {renderOrder()}
              <br />
              {renderFooter()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return parent.status === 'reject' ? null : (
    <div>
      {size === 'small' ? (
        <MDButton variant='text' color='info' size='small' iconOnly onClick={print}>
          <Icon>print</Icon> {t(`element.print`)}
        </MDButton>
      ) : (
        <MDButton variant='gradient' color='info' onClick={print}>
          <Icon>print</Icon>&nbsp;{t(`element.print`)}
        </MDButton>
      )}
      {renderLoading()}
      {renderPrint()}
    </div>
  );
}

PurchaseRequestPrint.defaultProps = {
  size: 'small',
};

PurchaseRequestPrint.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  size: PropTypes.string,
};

export default PurchaseRequestPrint;
